import { getAuctionPhase } from "@/lib/market";
import { z, type ZodType } from "zod";
import { ApiError, apiRequest, createFormBody } from "./client";
import { filterNumberSearchResults, parseSearchQuery } from "../nft-search";
import {
  activityFeedSchema,
  auctionSchema,
  auctionsSchema,
  collectionSchema,
  deviceSessionsSchema,
  popupNotificationsSchema,
  twoFactorSetupSchema,
  collectionsSchema,
  financeHistoryListSchema,
  favouritesSchema,
  nftDetailsSchema,
  nftHistoryListSchema,
  notificationsSchema,
  ownedNftsSchema,
  paymentMethodsSchema,
  paymentSessionSchema,
  searchResultsSchema,
  rankingSchema,
  userSchema,
  type CollectionStats,
  type NftExtra,
  type Portfolio,
  type RankingRow,
  type SeriesPoint
} from "./schemas";

const mutationResponseSchema = z.unknown().refine((value) => {
  if (value === false) return false;
  if (typeof value === "string" && /<(?:!doctype|html|body|head)\b/i.test(value)) return false;
  if (value && typeof value === "object") {
    const result = value as Record<string, unknown>;
    if (result.success === false || result.ok === false || result.error) return false;
  }
  return true;
});

function unavailable<T>(): Promise<T> {
  return Promise.reject(new ApiError(501, "endpoint_not_documented"));
}

function get<T>(path: string, schema: ZodType<T>, signal?: AbortSignal) {
  return apiRequest(path, schema, { method: "GET", signal });
}

function postForm<T = unknown>(
  path: string,
  values: Record<string, string | number | boolean | null | undefined>,
  schema: ZodType<T> = mutationResponseSchema as ZodType<T>,
  signal?: AbortSignal
) {
  return apiRequest(path, schema, {
    method: "POST",
    signal,
    body: createFormBody(values),
    headers: { "Content-Type": "application/x-www-form-urlencoded" }
  });
}

export const api = {
  login(email: string, password: string, remember: boolean, signal?: AbortSignal) {
    return postForm("/api/login", { email, password, remember: Number(remember) }, mutationResponseSchema, signal);
  },
  loginWithTwoFactor(preAuthToken: string, code: string, remember: boolean, signal?: AbortSignal) {
    return postForm("/api/login/2fa", { pre_auth_token: preAuthToken, code, remember: Number(remember) }, mutationResponseSchema, signal);
  },
  logout(signal?: AbortSignal) {
    return postForm("/api/logout", {}, mutationResponseSchema, signal);
  },
  signup(email: string, password: string, inviteCode: string, language: string, signal?: AbortSignal) {
    return postForm(
      "/api/signup",
      { email, password, ...(inviteCode.trim() ? { invite_code: inviteCode.trim() } : {}), language },
      mutationResponseSchema,
      signal
    );
  },
  resetPassword(email: string, password: string, signal?: AbortSignal) {
    return postForm("/api/reset_password", { email, password }, mutationResponseSchema, signal);
  },
  resendEmail(signal?: AbortSignal) {
    return get("/api/resend_email", mutationResponseSchema, signal);
  },
  checkActivationKey(key: string, signal?: AbortSignal) {
    return get(`/api/check_key?key=${encodeURIComponent(key)}`, mutationResponseSchema, signal);
  },
  getUser(signal?: AbortSignal) {
    return get("/api/user", userSchema, signal);
  },
  getCollections(signal?: AbortSignal) {
    return get("/api/get_collections", collectionsSchema, signal);
  },
  getCollection(collectionId: string, signal?: AbortSignal) {
    return get(`/api/get_collection_nft?collection_id=${encodeURIComponent(collectionId)}`, collectionSchema, signal);
  },
  getNft(imageId: string, signal?: AbortSignal) {
    return get(`/api/get_nft?image_id=${encodeURIComponent(imageId)}`, nftDetailsSchema, signal);
  },
  async search(query: string, signal?: AbortSignal) {
    const parsed = parseSearchQuery(query);
    const backendQuery = parsed.number === null ? parsed.text : `#${parsed.number}`;
    if (!backendQuery) return [];
    const results = await postForm("/api/search", { query: backendQuery }, searchResultsSchema, signal);
    return filterNumberSearchResults(results, parsed);
  },
  buyNft(imageId: string, signal?: AbortSignal) {
    return postForm("/api/buy_nft", { image_id: imageId }, mutationResponseSchema, signal);
  },
  sellNft(ownId: string, price: number, signal?: AbortSignal) {
    return postForm("/api/sell_nft", { own_id: ownId, price }, mutationResponseSchema, signal);
  },
  unsellNft(ownId: string, signal?: AbortSignal) {
    return postForm("/api/unsell_nft", { own_id: ownId }, mutationResponseSchema, signal);
  },
  getOwnedNfts(signal?: AbortSignal) {
    return get("/api/my_nfts", ownedNftsSchema, signal);
  },
  getFavourites(signal?: AbortSignal) {
    return get("/api/get_favourites", favouritesSchema, signal);
  },
  addFavourite(nftId: string, signal?: AbortSignal) {
    return postForm("/api/add_favourite", { nft_id: nftId }, mutationResponseSchema, signal);
  },
  deleteFavourite(nftId: string, signal?: AbortSignal) {
    return postForm("/api/del_favourite", { nft_id: nftId }, mutationResponseSchema, signal);
  },
  getNotifications(markRead = false, signal?: AbortSignal) {
    return get(`/api/user/notifications/fetch?mark_read=${markRead}`, notificationsSchema, signal);
  },
  getPaymentMethods(signal?: AbortSignal) {
    return get("/api/payment/methods", paymentMethodsSchema, signal);
  },
  createPayment(amount: number, method: number, signal?: AbortSignal) {
    return postForm("/api/payment/create", { amount, method }, paymentSessionSchema, signal);
  },
  checkPayment(id: string, signal?: AbortSignal) {
    return postForm("/api/payment/check", { id }, z.boolean(), signal);
  },
  getWithdrawMethods(signal?: AbortSignal) {
    return get("/api/withdraw/methods", paymentMethodsSchema, signal);
  },
  createWithdraw(amount: number, method: number, details: string, bank?: string, signal?: AbortSignal) {
    return postForm("/api/withdraw/create", { amount, method, details, bank }, mutationResponseSchema, signal);
  },
  getFinanceHistory(signal?: AbortSignal) {
    return get("/api/finance/history", financeHistoryListSchema, signal);
  },
  getNftHistory(signal?: AbortSignal) {
    return get("/api/my_nfts/history", nftHistoryListSchema, signal);
  },
  setLanguage(language: string, signal?: AbortSignal) {
    return postForm("/api/user/set_language", { key: language }, mutationResponseSchema, signal);
  },
  setCurrency(currency: string, signal?: AbortSignal) {
    return postForm("/api/user/set_currency", { key: currency }, mutationResponseSchema, signal);
  },
  setUsername(username: string, signal?: AbortSignal) {
    return postForm("/api/user/set_username", { key: username }, mutationResponseSchema, signal);
  },
  changeEmail(email: string, signal?: AbortSignal) {
    return postForm("/api/user/change_email", { email }, mutationResponseSchema, signal);
  },
  changePassword(password: string, oldPassword: string, signal?: AbortSignal) {
    return postForm("/api/user/change_password", { password, old_password: oldPassword }, mutationResponseSchema, signal);
  },
  getSessions(signal?: AbortSignal) {
    return postForm("/api/sessions", {}, deviceSessionsSchema, signal);
  },
  revokeSession(sessionId: string, signal?: AbortSignal) {
    return postForm("/api/sessions/revoke", { session_id: sessionId }, mutationResponseSchema, signal);
  },
  revokeOtherSessions(signal?: AbortSignal) {
    return postForm("/api/sessions/revoke-others", {}, mutationResponseSchema, signal);
  },
  setupTwoFactor(signal?: AbortSignal) {
    return postForm("/api/2fa/setup", {}, twoFactorSetupSchema, signal);
  },
  enableTwoFactor(code: string, signal?: AbortSignal) {
    return postForm("/api/2fa/enable", { code }, mutationResponseSchema, signal);
  },
  disableTwoFactor(signal?: AbortSignal) {
    return postForm("/api/2fa/disable", {}, mutationResponseSchema, signal);
  },
  getActivityFeed(count: number, signal?: AbortSignal, scope?: { collectionId?: string; nftId?: string; lang?: string; theme?: string; currency?: string }) {
    const query = new URLSearchParams({ count: String(count) });
    if (scope?.lang) query.set("lang", scope.lang);
    if (scope?.theme) query.set("theme", scope.theme);
    if (scope?.currency) query.set("currency", scope.currency);
    if (scope?.collectionId) query.set("collection_id", scope.collectionId);
    if (scope?.nftId) query.set("image_id", scope.nftId);
    return get(`/api/activity-feed?${query.toString()}`, activityFeedSchema, signal);
  },
  getCollectionStats(..._args: [id: string, signal?: AbortSignal]) {
    void _args;
    return unavailable<CollectionStats>();
  },
  getNftExtra(..._args: [id: string, signal?: AbortSignal]) {
    void _args;
    return unavailable<NftExtra>();
  },
  getAuction(imageId: string, signal?: AbortSignal) {
    return get(`/api/nft/auction?image_id=${encodeURIComponent(imageId)}`, auctionSchema, signal);
  },
  getAuctions(status: "active" | "upcoming" | "ended", options: { collectionId?: string; limit?: number } = {}, signal?: AbortSignal) {
    const query = new URLSearchParams({ status: String({ active: 0, upcoming: 1, ended: 2 }[status]), limit: String(options.limit ?? 20) });
    if (options.collectionId) query.set("collection_id", options.collectionId);
    return get(`/api/auctions?${query.toString()}`, auctionsSchema, signal).then((lots) => {
      const now = Date.now();
      return lots.filter((lot) => getAuctionPhase(lot, now).id === status);
    });
  },
  placeBid(imageId: string, amount: number, signal?: AbortSignal) {
    const query = new URLSearchParams({ image_id: imageId, amount: String(amount) });
    return apiRequest(`/api/nft/auction/bid?${query.toString()}`, mutationResponseSchema, { method: "POST", signal });
  },
  buyNowAuction(..._args: [id: string, signal?: AbortSignal]) {
    void _args;
    return unavailable<unknown>();
  },
  getPortfolio(..._args: [signal?: AbortSignal]) {
    void _args;
    return unavailable<Portfolio>();
  },
  getPortfolioHistory(..._args: [period: string, signal?: AbortSignal]) {
    void _args;
    return unavailable<SeriesPoint[]>();
  },
  getRankings(period: string, limit = 20, signal?: AbortSignal): Promise<RankingRow[]> {
    return postForm("/api/rankings", { period, limit }, rankingSchema, signal);
  },
  getPopupNotifications(signal?: AbortSignal) {
    return get("/api/user/popup_notifications/fetch?markRead=true", popupNotificationsSchema, signal);
  },
  setAvatar(file: File, signal?: AbortSignal) {
    const body = new FormData();
    body.set("file", file);
    return apiRequest("/api/user/avatar/set", mutationResponseSchema, { method: "POST", body, signal });
  }
};
